/** Owner-only integration keys (System → «Ключи интеграций»): API shapes of
 *  /api/admin/v1/system/secrets and the pure helpers of the edit form.
 *
 *  SECURITY: key VALUES live only in the edit form's component state while the owner types
 *  them and in the JSON body of the PUT / POST that sends them. They never go into a URL,
 *  localStorage, the TanStack Query cache (no useMutation with values: its variables stay in
 *  the mutation cache), console output or an error text. The API returns hints and
 *  fingerprints only. */
import type { QueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'

export type SecretSource = 'admin' | 'env' | 'none'
export type SecretType = 'secret' | 'text' | 'uuid' | 'url'

export interface SecretField {
  name: string
  label: string
  env: string
  type: SecretType
  required: boolean
  source: SecretSource
  /** «…a1b2» for a secret of 16+ chars, the full value for text / uuid / url, else null. */
  hint: string | null
  fingerprint: string | null
  updated_at: string | null
  updated_by: string | null
  /** The saved value does not decrypt; the .env value is in effect. */
  broken: boolean
  /** An API address saved here is held back: a key of its group would come from .env, and a
   *  .env key is never sent to an address typed in the admin. The .env address is in effect. */
  held_back?: boolean
  /** Which credential to paste (server registry); '' / absent when there is nothing to add. */
  help?: string | null
}

export interface PoolEntry {
  id: number
  label: string
  source: 'admin' | 'env'
  key_id_hint: string | null
  secret_hint: string | null
  enabled: boolean
  healthy: boolean
  updated_at: string | null
  /** A key added here whose saved value does not decrypt: the worker does not use it, and
   *  `enabled` / `healthy` say nothing about it. */
  broken: boolean
}

export interface SecretGroup {
  group: string
  label: string
  kind: 'ai' | 'payments'
  ready: boolean
  test_available: boolean
  note: string | null
  fields: SecretField[]
  /** Higgsfield only: the key pool (ENV pairs + keys added here). */
  pool: PoolEntry[] | null
}

export interface SecretsOverview {
  /** false: the kill switch (ADMIN_SECRETS_ENABLED=false) or the vault is unavailable. */
  editable: boolean
  /** false: the vault is unavailable or some saved values do not decrypt. */
  vault_ok: boolean
  /** false: the saved keys could not be read (database / migration); the .env values are in
   *  effect. Absent on an older server: treated as available. */
  vault_available?: boolean
  /** ENV names never edited here (server list); absent on an older server. */
  env_only?: string[]
  groups: SecretGroup[]
}

export interface SecretTestResult {
  ok: boolean
  message: string
}

/** What «Проверить» shows. ok true / false: the provider answered. ok null: the check never
 *  ran (rate limit, service or vault unavailable, a candidate the server rejected, no
 *  connection) — that says nothing about the key. */
export interface SecretTestOutcome {
  ok: boolean | null
  message: string
}

/** One field of the edit form. `remove` = «Удалить из админки» (sent as null). */
export interface FieldEdit {
  value: string
  remove: boolean
}
export type GroupDraft = Record<string, FieldEdit>

export const MAX_SECRET_LEN = 4096

export const SECRETS_QUERY_KEY = ['system-secrets'] as const

/** The .env-only sentence when the server does not send its `env_only` list. */
export const ENV_ONLY_FALLBACK =
  'BOT_TOKEN, ADMIN_JWT_SECRET, ENCRYPTION_KEY, строки подключения к базе, Redis и RabbitMQ, ' +
  'хранилище S3, METRICS_TOKEN, SENTRY_DSN и адреса API провайдеров, у которых есть значение по умолчанию'

export function isGroup(v: unknown): v is SecretGroup {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as SecretGroup).group === 'string' &&
    Array.isArray((v as SecretGroup).fields)
  )
}

/** A value the owner typed (trimmed); '' when the field is left as is. */
function typed(draft: GroupDraft, name: string): string {
  const e = draft[name]
  return e && !e.remove ? e.value.trim() : ''
}

/** The PUT body `values`: typed fields → the value, «Удалить из админки» → null, untouched
 *  fields are left out (unchanged on the server). */
export function buildValues(draft: GroupDraft): Record<string, string | null> {
  const out: Record<string, string | null> = {}
  for (const [name, e] of Object.entries(draft)) {
    if (e.remove) out[name] = null
    else if (e.value.trim()) out[name] = e.value.trim()
  }
  return out
}

/** Candidate values for «Проверить»: only what was typed (removals are not candidates). */
export function buildCandidates(draft: GroupDraft): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [name, e] of Object.entries(draft)) {
    if (!e.remove && e.value.trim()) out[name] = e.value.trim()
  }
  return out
}

/** «Удалить из админки» is offered for a value saved here, and for one that no longer
 *  decrypts (reported as env / none + broken): removing it is how the owner falls back to .env.
 *  The server deletes the row without decrypting it. */
export function removableFromAdmin(f: SecretField): boolean {
  return f.source === 'admin' || f.broken || f.held_back === true
}

/** Does the form change the group's API address? Typing the address that is already in
 *  effect (the hint of a url field is its full value) is not a change. */
export function urlEdited(group: SecretGroup, draft: GroupDraft): boolean {
  return group.fields.some((f) => {
    if (f.type !== 'url') return false
    const e = draft[f.name]
    if (!e) return false
    if (e.remove) return f.source === 'admin'
    const v = e.value.trim()
    return v !== '' && v !== (f.hint ?? '')
  })
}

/** URL binding rule (anti-exfiltration, SPEC 1.3): when the address changes, every secret of
 *  the group that is configured now must be typed again in the same save / test — a new
 *  address is never combined with a stored key. Returns the fields still missing. */
export function missingForUrlChange(group: SecretGroup, draft: GroupDraft): SecretField[] {
  if (!urlEdited(group, draft)) return []
  return group.fields.filter(
    (f) => f.type === 'secret' && f.source !== 'none' && typed(draft, f.name) === '',
  )
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
// Control characters (C0, DEL, C1) — never valid in any field.
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f]/

export function isUuid(v: string): boolean {
  return UUID_RE.test(v.trim())
}

function privateHost(host: string): boolean {
  const h = host.toLowerCase().replace(/^\[|\]$/g, '')
  if (h === 'localhost' || h.endsWith('.localhost')) return true
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h)) {
    const [a = 0, b = 0] = h.split('.').map(Number)
    return (
      a === 10 ||
      a === 127 ||
      a === 0 ||
      (a === 169 && b === 254) ||
      (a === 172 && b >= 16 && b <= 31) ||
      (a === 192 && b === 168)
    )
  }
  if (h.includes(':')) {
    return h === '::1' || h === '::' || h.startsWith('fe80') || h.startsWith('fc') || h.startsWith('fd')
  }
  return false
}

/** Client-side check before sending (the server checks again with the same rules). The text
 *  names the problem, NEVER the value. null = fine. An empty value is not an error here: it
 *  means «не менять». */
export function validateValue(type: SecretType, raw: string): string | null {
  const v = raw.trim()
  if (v === '') return null
  if (v.length > MAX_SECRET_LEN) return `Слишком длинное значение: больше ${MAX_SECRET_LEN} символов`
  if (CONTROL_RE.test(v)) return 'Есть служебные символы — скопируйте значение заново'
  if (type === 'text') return null
  if (/\s/.test(v)) return 'Внутри значения есть пробел или перенос строки'
  if (type === 'uuid' && !UUID_RE.test(v)) return 'Нужен UUID вида 123e4567-e89b-12d3-a456-426614174000'
  if (type === 'url') {
    let u: URL
    try {
      u = new URL(v)
    } catch {
      return 'Нужен адрес вида https://api.example.com'
    }
    if (u.protocol !== 'https:') return 'Адрес должен начинаться с https://'
    if (u.username || u.password) return 'Логин и пароль в адресе не допускаются'
    if (!u.hostname) return 'В адресе нет домена'
    if (u.search || u.hash || v.includes('?') || v.includes('#')) return 'Уберите из адреса параметры после ? и #'
    if (privateHost(u.hostname)) return 'Локальные и внутренние адреса не допускаются'
  }
  return null
}

/** name → error for every typed field of the draft. */
export function draftErrors(group: SecretGroup, draft: GroupDraft): Record<string, string> {
  const out: Record<string, string> = {}
  for (const f of group.fields) {
    const err = validateValue(f.type, typed(draft, f.name))
    if (err) out[f.name] = err
  }
  return out
}

/** Client-side check of a Higgsfield key pair (stored as «id:secret», so the id has no
 *  colon); the text never contains the value. */
export function poolPairError(keyId: string, keySecret: string): string | null {
  const id = keyId.trim()
  const secret = keySecret.trim()
  if (!id || !secret) return 'Нужны оба значения: ID ключа и секрет'
  if (id.includes(':')) return 'В ID ключа не может быть двоеточия'
  return validateValue('secret', id) ?? validateValue('secret', secret)
}

export function isTotpCode(v: string): boolean {
  return /^\d{6}$/.test(v)
}

/** Digits only, at most 6 (a pasted «123 456» becomes «123456»). */
export function cleanTotp(v: string): string {
  return v.replace(/\D/g, '').slice(0, 6)
}

/** Errors after which the 2FA dialog stays open for another code: wrong / reused code (403),
 *  too many attempts (429), the check being unavailable (503). Anything else (a value the
 *  server rejected, a changed address without keys, …) closes it and goes back to the form. */
export function keepStepUpOpen(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 403 || e.status === 429 || e.status === 503)
}

/** Words for a failed save / pool change. 429 keeps the server's explanation of the lock and
 *  adds the wait from Retry-After. */
export function stepUpErrorText(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Нет связи с сервером. Попробуйте ещё раз'
  if (e.status === 429) {
    const base = e.detail ?? 'Слишком много попыток ввести код 2FA'
    return e.retryAfter && !/повторите/i.test(base) ? `${base}. Повторите через ${e.retryAfter}` : base
  }
  return e.message
}

export const SOURCE_LABEL: Record<SecretSource, string> = {
  admin: 'Админка',
  env: '.env',
  none: 'Не задан',
}

export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
}

export type VaultBanner = 'unavailable' | 'disabled' | 'broken'

/** Which page-wide banners the overview calls for. When the vault cannot be read the server
 *  also answers editable=false and vault_ok=false; that is neither the kill switch nor a
 *  decrypt failure, so only «unavailable» is shown. */
export function vaultBanners(o: SecretsOverview): VaultBanner[] {
  if (o.vault_available === false) return ['unavailable']
  const out: VaultBanner[] = []
  if (!o.editable) out.push('disabled')
  if (!o.vault_ok) out.push('broken')
  return out
}

export interface PoolBadge {
  tone: 'ok' | 'neutral' | 'danger'
  label: string
}

/** State badges of a pool key (dot + word). An undecryptable key is only «Не расшифровывается»:
 *  its enabled / healthy flags come from rotation state it never had. */
export function poolEntryBadges(k: PoolEntry): PoolBadge[] {
  if (k.broken) return [{ tone: 'danger', label: 'Не расшифровывается' }]
  return [
    k.enabled ? { tone: 'ok', label: 'Включён' } : { tone: 'neutral', label: 'Выключен' },
    k.healthy ? { tone: 'ok', label: 'Здоров' } : { tone: 'danger', label: 'Снят с ротации' },
  ]
}

/** A failed «Проверить» request: it never reached the provider (the provider's own verdict comes
 *  back as 200 {ok, message}). */
export function testNotRun(e: unknown): SecretTestOutcome {
  return { ok: null, message: e instanceof ApiError ? e.message : 'Нет связи с сервером' }
}

/** Runs «Проверить» and turns the answer into what the card shows. */
export async function runSecretTest(run: () => Promise<SecretTestResult>): Promise<SecretTestOutcome> {
  let r: SecretTestResult | undefined
  try {
    r = await run()
  } catch (e) {
    return testNotRun(e)
  }
  const ok = r?.ok === true
  const message = typeof r?.message === 'string' && r.message ? r.message : ok ? 'Ключ принят' : 'Провайдер не ответил'
  return { ok, message }
}

/** The names that are edited only in .env: the server's list when it sends one. */
export function envOnlyText(o: SecretsOverview | undefined): string {
  const list: unknown = o?.env_only
  const names = Array.isArray(list) ? list.filter((n): n is string => typeof n === 'string' && n.trim() !== '') : []
  return names.length > 0 ? names.join(', ') : ENV_ONLY_FALLBACK
}

/** After a successful PUT: the returned group at once, then a refetch of the whole overview —
 *  vault_ok / editable are page-wide (a re-entered broken key clears the red banner only via
 *  a GET). The query cache holds GET answers only, never a value. */
export function applySavedGroup(qc: QueryClient, res: unknown): void {
  if (isGroup(res)) {
    qc.setQueryData<SecretsOverview>(SECRETS_QUERY_KEY, (old) =>
      old ? { ...old, groups: old.groups.map((x) => (x.group === res.group ? res : x)) } : old,
    )
  }
  void qc.invalidateQueries({ queryKey: SECRETS_QUERY_KEY })
}
